import { useState } from "react";
import { toast } from "sonner";
import {
  PlayIcon,
  PauseIcon,
  ArrowsClockwiseIcon,
  XIcon,
} from "@phosphor-icons/react";

import {
  Field,
  Modal,
  PrimaryButton,
  SecondaryButton,
  DangerButton,
  TextArea,
} from "../crm/Form";
import api, { formatApiError } from "../../lib/api";

/**
 * Renders only the lifecycle buttons that are valid given current status.
 * Office-only (page caller is responsible for hiding entirely from techs).
 */
export default function SubscriptionActionBar({
  subscription,
  onChange,
}) {
  const status = subscription.status;
  const [busy, setBusy] = useState(false);
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseReason, setPauseReason] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(true);

  const call = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/subscriptions/${subscription.id}/${path}`,
        body || {}
      );
      onChange(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="subscription-action-bar"
      className="flex flex-wrap gap-2"
    >
      {status === "pending" && (
        <PrimaryButton
          type="button"
          disabled={busy}
          onClick={() => call("activate")}
          data-testid="sub-activate"
        >
          <PlayIcon size={14} weight="bold" />
          Activate
        </PrimaryButton>
      )}
      {status === "active" && (
        <>
          <SecondaryButton
            type="button"
            disabled={busy}
            onClick={() => setPauseOpen(true)}
            data-testid="sub-pause"
          >
            <PauseIcon size={14} weight="bold" />
            Pause
          </SecondaryButton>
          <SecondaryButton
            type="button"
            disabled={busy}
            onClick={() => call("renew")}
            data-testid="sub-renew"
          >
            <ArrowsClockwiseIcon size={14} weight="bold" />
            Renew period
          </SecondaryButton>
        </>
      )}
      {status === "paused" && (
        <PrimaryButton
          type="button"
          disabled={busy}
          onClick={() => call("resume")}
          data-testid="sub-resume"
        >
          <PlayIcon size={14} weight="bold" />
          Resume
        </PrimaryButton>
      )}
      {(status === "active" || status === "paused" || status === "pending") && (
        <DangerButton
          type="button"
          disabled={busy}
          onClick={() => setCancelOpen(true)}
          data-testid="sub-cancel"
        >
          <XIcon size={14} weight="bold" />
          Cancel
        </DangerButton>
      )}
      {status === "lapsed" && (
        <PrimaryButton
          type="button"
          disabled={busy}
          onClick={() => call("activate")}
          data-testid="sub-reactivate"
        >
          <PlayIcon size={14} weight="bold" />
          Reactivate
        </PrimaryButton>
      )}

      {/* Pause modal */}
      <Modal
        open={pauseOpen}
        onClose={() => setPauseOpen(false)}
        title="Pause Subscription"
        subtitle="No charges accrue while paused"
        data-testid="pause-modal"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            call("pause", { reason: pauseReason || null });
            setPauseOpen(false);
            setPauseReason("");
          }}
          className="space-y-4"
        >
          <Field label="Reason (optional)">
            <TextArea
              value={pauseReason}
              onChange={setPauseReason}
              rows={3}
              data-testid="pause-reason"
              placeholder="snowbird out till April; customer hospital stay; etc."
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setPauseOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" disabled={busy} data-testid="pause-confirm">
              Pause
            </PrimaryButton>
          </div>
        </form>
      </Modal>

      {/* Cancel modal */}
      <Modal
        open={cancelOpen}
        onClose={() => setCancelOpen(false)}
        title="Cancel Subscription"
        subtitle={
          cancelAtPeriodEnd
            ? "Stays active until the current period ends"
            : "Effective immediately"
        }
        data-testid="cancel-modal"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            call("cancel", {
              reason: cancelReason || null,
              at_period_end: status === "active" ? cancelAtPeriodEnd : false,
            });
            setCancelOpen(false);
            setCancelReason("");
          }}
          className="space-y-4"
        >
          {status === "active" && (
            <div className="flex items-center gap-2">
              <input
                id="cancel-at-period-end"
                type="checkbox"
                checked={cancelAtPeriodEnd}
                onChange={(e) => setCancelAtPeriodEnd(e.target.checked)}
                data-testid="cancel-at-period-end"
                className="h-4 w-4 rounded border-slate-300 text-amber-600"
              />
              <label htmlFor="cancel-at-period-end" className="text-xs text-slate-700">
                Cancel at period end (preserves coverage until{" "}
                {subscription.current_period_end || "—"})
              </label>
            </div>
          )}
          <Field label="Reason (optional)">
            <TextArea
              value={cancelReason}
              onChange={setCancelReason}
              rows={3}
              data-testid="cancel-reason"
              placeholder="customer moved; downgrade requested; etc."
            />
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <SecondaryButton type="button" onClick={() => setCancelOpen(false)}>
              Keep
            </SecondaryButton>
            <DangerButton type="submit" disabled={busy} data-testid="cancel-confirm">
              Cancel
            </DangerButton>
          </div>
        </form>
      </Modal>
    </div>
  );
}
